import { ChangeEvent, FC, useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { useDebounce } from "src/libs/hooks";
import { AxiosError } from "axios";
import useHashtags from "../../hooks/useHashtags";
import {
	useController,
	useFormContext,
	useFormState,
	useWatch
} from "react-hook-form";
import get from "lodash.get";
import { useParams } from "react-router-dom";
import useHashtag from "../../hooks/useHashtag";

interface Props {
	field: BannerMultiField;
	stepNumber: number;
}

const HashtagSearch: FC<Props> = ({ field, stepNumber }) => {
	const params = useParams<{ formId: number }>();
	const [isOpen, setIsOpen] = useState(false);
	const [debouncedSearchValue, setDebouncedSearchValue] = useState<
		string | undefined
	>();
	const [isCreateNew, setIsCreateNew] = useState(false);
	const hashtagListboxId = useId();

	const listInnerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		data,
		isError,
		error,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useHashtags(debouncedSearchValue, {
		enabled: Boolean(debouncedSearchValue)
	});

	const { control, setValue } = useFormContext<BannerFormState>();

	const bannerStyle = useWatch({
		control,
		name: "bannerStyle"
	});

	const { field: inputValue } = useController({
		name: `steps.${stepNumber - 1}.fields.${field.order - 1}.givenAnswer`,
		control,
		rules: { required: bannerStyle === "campaign" }
	});

	const onChange = (event: ChangeEvent<HTMLInputElement>) => {
		inputValue.onChange(event);
		setIsOpen(true);
	};

	const selectedHashtag = useWatch({
		control,
		name: "selectedHashtag"
	});

	const placeholder = useWatch({
		control,
		name: `steps.${stepNumber - 1}.fields.${field.order - 1}.placeholder`
	});

	const { refetch } = useHashtag(inputValue.value, { enabled: false });

	useEffect(() => {
		if (!params.formId) return;

		(async () => {
			const { data } = await refetch();

			if (
				data &&
				data.results.length &&
				data.results[0].name === inputValue.value
			) {
				setValue("selectedHashtag", 1);
				setValue("feedId", data.results[0].uuid);
			}
		})();
	}, [params]);

	const onClick = (name: string, uuid: string, index: number) => {
		setValue(
			`steps.${stepNumber - 1}.fields.${field.order - 1}.givenAnswer`,
			name
		);
		setValue("selectedHashtag", index);
		setValue("feedId", uuid);
		setIsOpen(false);
	};

	const onClear = () => {
		setValue("selectedHashtag", null);
		setValue("feedId", null);
		setIsCreateNew(false);
		setIsOpen(false);
		setValue(
			`steps.${stepNumber - 1}.fields.${field.order - 1}.givenAnswer`,
			""
		);
	};

	const onCreateNew = () => {
		if (!debouncedSearchValue || !inputRef.current) return;

		inputRef.current.focus();
		setIsCreateNew(true);
		setIsOpen(false);
		setValue("selectedHashtag", 1);
		setValue(
			`steps.${stepNumber - 1}.fields.${field.order - 1}.givenAnswer`,
			debouncedSearchValue
		);
	};

	useDebounce(() => setDebouncedSearchValue(field.givenAnswer), 300, [
		field.givenAnswer
	]);

	const handleScroll = () => {
		if (listInnerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
			if (scrollTop + clientHeight === scrollHeight) {
				console.log("reached bottom");
				hasNextPage && fetchNextPage();
			}
		}
	};

	const { errors } = useFormState({ control });

	const isAnswerInvalid = Boolean(
		get(errors, `steps[${stepNumber - 1}].fields[${field.order - 1}]`, null)
	);

	const hashtags = data ? data?.pages.flatMap(({ results }) => results) : [];

	const createNew = [{ name: "Create new #" }];

	if (isError && error)
		return (
			<span className="text-red-600">{(error as AxiosError).message}</span>
		);

	const hasError =
		(Boolean(!selectedHashtag) && isAnswerInvalid) ||
		(inputValue.value.length && Boolean(!selectedHashtag)) ||
		(isCreateNew && !inputValue.value.length) ||
		isAnswerInvalid;

	return (
		<div>
			<div className="relative">
				<div className="relative flex items-center">
					<span
						className={
							inputValue.value.length ? "text-foreground" : "text-zinc-600"
						}
						style={{ position: "absolute", left: 10, zIndex: 1 }}
					>
						{placeholder}
					</span>
					<input
						{...inputValue}
						onChange={(e) => onChange(e)}
						className={[
							"w-full bg-white border rounded pl-8 pr-10 py-2 text-sm focus:outline-none",
							hasError ? "border-red-500" : "border-transparent"
						].join(" ")}
						aria-label="Search hashtags"
						aria-controls={
							isOpen && !isCreateNew ? hashtagListboxId : undefined
						}
						aria-expanded={isOpen && !isCreateNew}
						readOnly={!isCreateNew && Boolean(selectedHashtag)}
						ref={inputRef}
					/>
					<div className="absolute right-2">
						{isFetchingNextPage && (
							<div
								className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"
								data-testid="spinner"
							/>
						)}
						{!isFetchingNextPage && Boolean(selectedHashtag) && (
							<button
								type="button"
								className="p-0 min-w-0"
								onClick={onClear}
								aria-label="Clear hashtag selection"
							>
								<X />
							</button>
						)}
					</div>
				</div>

				{isOpen && !isCreateNew && (
					<div
						id={hashtagListboxId}
						ref={listInnerRef}
						onScroll={handleScroll}
						aria-label="List of hashtags"
						role="listbox"
						className="absolute z-10 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-y-auto w-full"
					>
						{hashtags &&
							hashtags
								.map(({ name, uuid }, index) => (
									<button
										type="button"
										key={`option-${name}`}
										className="w-full text-left"
										onClick={() => onClick(name, uuid, index + 1)}
										role="option"
									>
										<div className="bg-foreground/5 py-1 px-2">
											<span className="text-sm">{name}</span>
										</div>
									</button>
								))
								.concat(
									createNew.map(({ name }) => (
										<button
											type="button"
											className="text-left ml-4 mt-2 text-sm w-fit rounded bg-foreground/10 px-2 hover:opacity-80"
											key={name}
											onClick={onCreateNew}
											role="option"
										>
											{name}
										</button>
									))
								)}
					</div>
				)}
			</div>

			{isCreateNew && (
				<p className="text-sm mt-4">
					Please note: Adding a new hashtag here will also make it available for
					users to filter on the app social feed.
				</p>
			)}
		</div>
	);
};

export default HashtagSearch;
